import React from 'react';
import { DetailSection } from '../common';
import { format, parseISO } from 'date-fns';
import {
    FaBus,
    FaTicketAlt,
    FaCalendarAlt,
    FaClock,
    FaGlobe,
    FaUser,
    FaSuitcase,
    FaArrowRight,
    FaMapMarkerAlt
} from 'react-icons/fa';
import { calculateFlightDuration } from '@trip-planner-monorepo/shared/utils/timeUtils';

/**
 * Bus-specific details display
 */
const BusDetails = ({ travel, tripId, RelatedDocumentsSection, onUnlink }) => {
    const { details, cost } = travel;

    if (!details) {
        return <div className="error-message">No bus details available</div>;
    }

    // Debug logs to help identify issues
    console.log("Bus details being displayed:", details);

    // Format date for display
    const formatDateTime = (dateStr) => {
        if (!dateStr) return { date: 'Not specified', time: '' };

        try {
            const date = parseISO(dateStr);
            return {
                date: format(date, 'MMM d, yyyy'),
                time: format(date, 'h:mm a')
            };
        } catch (e) {
            console.error("Error parsing date:", e);
            return { date: 'Not specified', time: '' };
        }
    };

    // Format stop info - improved to handle missing data better
    const getStopInfo = (stopName, city) => {
        if (!stopName && !city) {
            return {
                name: 'Not specified',
                city: 'Not specified',
                fullDisplay: 'Not specified'
            };
        }

        // If we have either stop or city, use that
        return {
            name: stopName || 'Not specified',
            city: city || 'Not specified',
            fullDisplay: stopName
                ? `${stopName}${city ? ` (${city})` : ''}`
                : city || 'Not specified'
        };
    };

    // Calculate bus journey duration
    const calculateDuration = () => {
        if (!details.departureTime || !details.arrivalTime) return null;

        try {
            // Use the utility function that uses realistic journey durations
            const durationResult = calculateFlightDuration(
                details.departureTime,
                details.arrivalTime,
                details.departureTimezone,
                details.arrivalTimezone
            );

            return durationResult.formatted;
        } catch (e) {
            console.error('Error calculating bus journey duration:', e);
            return null;
        }
    };

    // Format currency
    const formatCurrency = (amount, currency = 'USD') => {
        if (amount === undefined || amount === null || amount === 0) return 'Not specified';

        try {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: currency
            }).format(amount);
        } catch (e) {
            return `${amount} ${currency}`;
        }
    };

    // Format departure and arrival information
    // Debug raw date values
    console.log('Raw date values:', {
        departureTime: details.departureTime,
        arrivalTime: details.arrivalTime
    });

    const departure = {
        stop: getStopInfo(details.departureStop, details.departureCity),
        datetime: formatDateTime(details.departureTime),
        timezone: details.departureTimezone || 'Local time',
        platform: details.platform || ''
    };

    const arrival = {
        stop: getStopInfo(details.arrivalStop, details.arrivalCity),
        datetime: formatDateTime(details.arrivalTime),
        timezone: details.arrivalTimezone || 'Local time'
    };

    const duration = calculateDuration();
    const hasPassengers = Array.isArray(details.passengers) && details.passengers.length > 0;

    return (
        <div className="bus-details">
            {/* Bus Route Card */}
            <div className="bus-route-card">
                <div className="bus-route">
                    {/* Departure Information */}
                    <div className="departure-info">
                        <div className="city-name">{String(departure.stop.city)}</div>
                        <div className="station-name">{String(departure.stop.name)}</div>
                        <div className="time">{String(departure.datetime.time)}</div>
                        <div className="date">{String(departure.datetime.date)}</div>
                        <div className="timezone">{String(departure.timezone)}</div>
                        {departure.platform && (
                            <div className="platform">
                                <FaMapMarkerAlt className="platform-icon" /> Platform/Gate {departure.platform}
                            </div>
                        )}
                    </div>

                    {/* Bus Journey Divider with Bus Info */}
                    <div className="journey-divider">
                        <div className="operator-logo">
                            <FaBus /> <span className="operator-name">{details.operator || 'N/A'}</span>
                        </div>

                        {duration && (
                            <div className="journey-duration">
                                <FaClock /> {duration}
                            </div>
                        )}

                        <div className="journey-line"></div>

                        <div className="bus-number">
                            Bus {details.serviceNumber || 'N/A'}
                        </div>
                    </div>

                    {/* Arrival Information */}
                    <div className="arrival-info">
                        <div className="city-name">{String(arrival.stop.city)}</div>
                        <div className="station-name">{String(arrival.stop.name)}</div>
                        <div className="time">{String(arrival.datetime.time)}</div>
                        <div className="date">{String(arrival.datetime.date)}</div>
                        <div className="timezone">{String(arrival.timezone)}</div>
                    </div>
                </div>
            </div>

            {/* Related Documents moved to StandardDetailsLayout */}

            {/* Booking Information */}
            <DetailSection title="Booking Information">
                <div className="detail-item">
                    <span className="detail-label">Booked through:</span>
                    <span className="detail-value">{details.bookingAgent || 'Not specified'}</span>
                </div>
                <div className="detail-item">
                    <span className="detail-label">Booking reference:</span>
                    <span className="detail-value">{details.bookingReference || 'Not specified'}</span>
                </div>
                <div className="detail-item">
                    <span className="detail-label">Cost:</span>
                    <span className="detail-value">
                        {cost && cost.amount > 0
                            ? `${formatCurrency(cost.amount, cost.currency)} (${cost.paid ? 'Paid' : 'Unpaid'})`
                            : 'Not specified'
                        }
                    </span>
                </div>
                {details.ticketNumbers && details.ticketNumbers.length > 0 && (
                    <div className="detail-item">
                        <span className="detail-label">Ticket number(s):</span>
                        <span className="detail-value">
                            {details.ticketNumbers.join(', ')}
                        </span>
                    </div>
                )}
            </DetailSection>

            {/* Passenger Information */}
            {hasPassengers && (
                <DetailSection title="Passenger Information">
                    <div className="passengers-list">
                        {details.passengers.map((passenger, index) => (
                            <div key={index} className="passenger-item">
                                <div className="passenger-header">
                                    <FaUser className="passenger-icon" />
                                    <span className="passenger-name">
                                        {passenger.firstName || passenger.lastName
                                            ? `${passenger.firstName || ''} ${passenger.lastName || ''}`
                                            : `Passenger ${index + 1}`
                                        }
                                    </span>
                                </div>
                                {passenger.seat && (
                                    <div className="passenger-detail">
                                        <span className="detail-label">Seat:</span>
                                        <span className="detail-value">{passenger.seat}</span>
                                    </div>
                                )}
                                {passenger.foodPreference && passenger.foodPreference !== 'none' && (
                                    <div className="passenger-detail">
                                        <span className="detail-label">Food preference:</span>
                                        <span className="detail-value">
                                            {passenger.foodPreference.charAt(0).toUpperCase() +
                                                passenger.foodPreference.slice(1)}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </DetailSection>
            )}

            {/* Additional Notes */}
            {details.notes && (
                <DetailSection title="Additional Notes">
                    <div className="notes-container">
                        {details.notes}
                    </div>
                </DetailSection>
            )}
        </div>
    );
};

export default BusDetails; 